import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Table,
  Card,
  Button,
  Space,
  message,
  Popconfirm,
  Alert,
  Tag,
  Typography,
} from 'antd';
import {
  EditOutlined,
  DeleteOutlined,
  CopyOutlined,
} from '@ant-design/icons';
import apiService from '../../services/api';
import { formatDate } from '../../utils/helpers';
import { formatCurrency } from '../../utils/formatters';
import { useChannels } from '../../hooks/useChannels';
import EmptyState from '../../components/EmptyState';
import JobStatsCards from './JobStatsCards';
import JobFilters from './JobFilters';
import dayjs from 'dayjs';

const initialDateFilter = {
  year: dayjs().year(),
  month: dayjs().month(),
};

const JobList = () => {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [vans, setVans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({
    totalJobs: 0,
    filteredJobs: 0,
    completedJobs: 0,
    unassignedJobs: 0,
  });
  const [filters, setFilters] = useState({
    search: '',
    status: [],
    plPosition: '',
    van: '',
    channel: '',
    paymentStatus: '',
  });
  const [dateFilter, setDateFilter] = useState(initialDateFilter);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [filteredTotal, setFilteredTotal] = useState(0);
  const { channels } = useChannels();

  const handleDateFilterChange = (newValue) => {
    // Only update if the mode or value actually changes
    const modeChanged =
      (!!newValue.dateRange !== !!dateFilter.dateRange) ||
      (newValue.year !== dateFilter.year) ||
      (newValue.month !== dateFilter.month);

    let rangeChanged = false;
    if (newValue.dateRange && dateFilter.dateRange) {
      rangeChanged =
        newValue.dateRange[0]?.format('YYYY-MM-DD') !== dateFilter.dateRange[0]?.format('YYYY-MM-DD') ||
        newValue.dateRange[1]?.format('YYYY-MM-DD') !== dateFilter.dateRange[1]?.format('YYYY-MM-DD');
    }

    if (modeChanged || rangeChanged) {
      setDateFilter(newValue);
    }
  };

  const fetchData = useCallback(async (page = currentPage, limit = pageSize) => {
    try {
      setLoading(true);
      setError(null);
      // Build params based on dateFilter mode
      const params = {
        search: filters.search,
        status: filters.status.length > 0 ? filters.status : undefined,
        plPosition: filters.plPosition,
        channel: filters.channel,
        van: filters.van,
        paymentStatus: filters.paymentStatus || undefined,
        page,
        limit,
      };
      if (dateFilter.dateRange) {
        params.startDate = dateFilter.dateRange[0]?.format('YYYY-MM-DD');
        params.endDate = dateFilter.dateRange[1]?.format('YYYY-MM-DD');
      } else if (typeof dateFilter.year !== 'undefined') {
        params.year = dateFilter.year;
        if (typeof dateFilter.month !== 'undefined') {
          params.month = dateFilter.month;
        }
      }
      const [jobsResponse, vansResponse] = await Promise.all([
        apiService.jobs.getAll(params),
        apiService.vans.getAll(),
      ]);

      // Expecting jobsResponse.data to be { data: [...], total: N, filteredTotal: N }
      const jobsData = jobsResponse.data.data || [];
      setJobs(Array.isArray(jobsData) ? jobsData : []);
      setFilteredTotal(jobsResponse.data.filteredTotal || 0);
      setVans(vansResponse.data || []);

      // Use counts directly from the API response
      setStats({
        totalJobs: jobsResponse.data.total || 0,
        filteredJobs: jobsResponse.data.filteredTotal || 0,
        completedJobs: jobsResponse.data.completedTotal || 0,
        unassignedJobs: jobsResponse.data.unassignedTotal || 0,
      });
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to load jobs. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [filters, dateFilter, currentPage, pageSize]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);


  const handleDelete = useCallback(async (id) => {
    try {
      await apiService.jobs.delete(id);
      message.success('Job deleted successfully');
      fetchData();
    } catch (error) {
      console.error('Error deleting job:', error);
      message.error('Failed to delete job');
    }
  }, [fetchData]);

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed':
        return 'success';
      case 'assigned':
        return 'processing';
      case 'cancelled':
        return 'error';
      case 'unassigned':
        return 'warning';
      default:
        return 'default';
    }
  };

  const columns = useMemo(() => [
    {
      title: 'Reference',
      dataIndex: 'bookingReference',
      key: 'bookingReference',
      render: (text, record) => (
        <Typography.Link
          href={`/jobs/${record._id}`}
          onClick={e => { if (e.ctrlKey || e.metaKey || e.button === 1) return; e.preventDefault(); navigate(`/jobs/${record._id}`); }}
        >
          {text || 'No Reference'}
        </Typography.Link>
      ),
    },
    {
      title: 'Driver',
      dataIndex: ['driver', 'name'],
      key: 'driver',
      render: (text, record) => text || record.driver?.name || 'Unassigned',
    },
    {
      title: 'Van',
      dataIndex: ['van', 'registration'],
      key: 'van',
      render: (text, record) => text || record.van?.registration || 'Unassigned',
    },
    {
      title: 'Date',
      dataIndex: 'date',
      key: 'date',
      render: (date) => formatDate(date),
    },
    {
      title: 'Status',
      dataIndex: 'jobStatus',
      key: 'jobStatus',
      render: (jobStatus) => (
        <Tag color={getStatusColor(jobStatus)}>
          {jobStatus?.toUpperCase() || 'N/A'}
        </Tag>
      ),
    },
    {
      title: 'Net Value',
      dataIndex: ['payment', 'amount'],
      key: 'netValue',
      align: 'right',
      render: (amount, record) => {
        if (!amount && !record.payment?.amount) return 'N/A';
        return formatCurrency(amount || record.payment?.amount);
      },
    },
    {
      title: 'PL Position',
      dataIndex: 'plPosition',
      key: 'plPosition',
      render: (plPosition) => (
        <Tag color={plPosition === 'income' ? 'green' : 'volcano'}>
          {plPosition?.toUpperCase()}
        </Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button
            icon={<CopyOutlined />}
            onClick={() => navigate(`/jobs/create?clone=${record._id}`)}
          >
            Clone
          </Button>
          <Button
            type="primary"
            icon={<EditOutlined />}
            onClick={() => navigate(`/jobs/edit/${record._id}`)}
          >
            Edit
          </Button>
          <Popconfirm
            title="Are you sure you want to delete this job?"
            onConfirm={() => handleDelete(record._id)}
            okText="Yes"
            cancelText="No"
            okButtonProps={{ danger: true }}
          >
            <Button danger icon={<DeleteOutlined />}>
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ], [navigate, handleDelete]);

  return (
    <div>
      {error && (
        <Alert
          message="Failed to load jobs"
          description={error}
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          action={<Button size="small" onClick={() => fetchData()}>Retry</Button>}
        />
      )}

      <JobStatsCards loading={loading} stats={stats} />

      <Card style={{ marginTop: 16 }}>
        <JobFilters
          filters={filters}
          setFilters={setFilters}
          dateFilter={dateFilter}
          onDateFilterChange={handleDateFilterChange}
          vans={vans}
          channels={channels}
          onSearch={() => fetchData(1, pageSize)}
          onCreateJob={() => navigate('/jobs/create')}
        />
        <Table
          columns={columns}
          dataSource={jobs}
          rowKey="_id"
          loading={loading}
          locale={{ emptyText: <EmptyState message="No jobs found" hint="Try adjusting your filters or date range" /> }}
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: filteredTotal,
            showSizeChanger: true,
            onChange: (page, pageSize) => {
              setCurrentPage(page);
              setPageSize(pageSize);
            },
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} jobs`,
          }}
        />
      </Card>
    </div>
  );
};

export default JobList; 